/**
 * Lookup service: calls httpService (data access), converts DAOs to FE-consumable models.
 * Implement fetch/convert for resources as we discover need; httpService has full coverage.
 */

import { getApiBaseUrl } from "../../functions/functions"
import * as http from "./httpService"
import type { EntityDAO, InstrumentGroupDAO } from "./types"
import type {
	EntityKeyFields,
	InstrumentGroupCovenantRow,
	InstrumentGroupInstrumentRow,
	InstrumentGroupKeyFields,
	InstrumentGroupParticipantRow,
	InstrumentGroupUseOfProceedsRow,
	TableRow,
} from "./types"

const FALLBACK = "—"

function str(val: unknown): string {
	if (val == null) return FALLBACK
	const s = String(val).trim()
	return s || FALLBACK
}

function ensureArray<T>(val: unknown): T[] {
	if (val == null) return []
	return Array.isArray(val) ? (val as T[]) : ([val] as T[])
}

function pick(
	obj: Record<string, unknown> | null | undefined,
	keys: string[],
): Record<string, string | number | boolean> {
	if (obj == null || typeof obj !== "object") return {}
	const out: Record<string, string | number | boolean> = {}
	for (const k of keys) {
		const v = obj[k]
		if (v === true || v === false) out[k] = v
		else if (typeof v === "number") out[k] = v
		else out[k] = str(v)
	}
	return out
}

// ---------------------------------------------------------------------------
// Base URL helper (inject for tests)
// ---------------------------------------------------------------------------

function baseUrl(): string {
	return getApiBaseUrl()
}

// ---------------------------------------------------------------------------
// Entity: fetch + conversion
// ---------------------------------------------------------------------------

export async function fetchEntity(id: string): Promise<EntityDAO> {
	return http.getEntityById(baseUrl(), id)
}

export function entityDaoToKeyFields(data: EntityDAO): EntityKeyFields {
	return {
		creditHqEntityIdentifier: str(
			data.creditHqEntityIdentifier ?? (data as Record<string, unknown>).credit_hq_entity_identifier,
		),
		entityNameLongEnglish: str(
			data.entityNameLongEnglish ?? (data as Record<string, unknown>).entity_name_long_english,
		),
		entityDisplayName: str(
			data.entityDisplayName ?? (data as Record<string, unknown>).entity_display_name,
		),
		commonStreetName: str(
			data.commonStreetName ?? (data as Record<string, unknown>).common_street_name,
		),
		doingBusinessAs: str(
			data.doingBusinessAs ?? (data as Record<string, unknown>).doing_business_as,
		),
		websiteUrl: str(data.websiteUrl ?? (data as Record<string, unknown>).website_url),
		shortBusinessDescription: str(
			data.shortBusinessDescription ??
				(data as Record<string, unknown>).short_business_description,
		),
		fiscalYearEnd: str(
			data.fiscalYearEnd ?? (data as Record<string, unknown>).fiscal_year_end,
		),
	}
}

export function extractEntityKeyFields(data: EntityDAO): EntityKeyFields {
	return entityDaoToKeyFields(data)
}

export function extractIncorporationRows(
	data: EntityDAO,
): Array<Record<string, string | number | boolean>> {
	const inc = (data as Record<string, unknown>).incorporation as
		| Record<string, unknown>
		| null
		| undefined
	if (inc == null) return []
	return [
		pick(inc, [
			"incorporationCode",
			"incorporationName",
			"incorporationCountryNameShort",
		]),
	]
}

export function extractSectorsRows(data: EntityDAO): TableRow[] {
	return ensureArray<Record<string, unknown>>(
		(data as Record<string, unknown>).sectors,
	).map((s) =>
		pick(s, [
			"marketName",
			"sectorName",
			"subsectorName",
			"industryName",
			"isPrimarySectorClassification",
		]),
	)
}

export function extractDomicileRows(
	data: EntityDAO,
): Array<Record<string, string | number | boolean>> {
	const dom = (data as Record<string, unknown>).domicile as
		| Record<string, unknown>
		| null
		| undefined
	if (dom == null) return []
	return [
		pick(dom, [
			"firstLineStreetAddress",
			"locality",
			"postalCode",
			"domicileCountryNameShort",
		]),
	]
}

export function extractMappingsRows(data: EntityDAO): TableRow[] {
	return ensureArray<Record<string, unknown>>(
		(data as Record<string, unknown>).mappings,
	).map((m) =>
		pick(m, [
			"externalProviderName",
			"externalProviderIdentifier",
			"externalProviderProvidedName",
		]),
	)
}

export function extractParentEntitiesRows(data: EntityDAO): TableRow[] {
	return ensureArray<Record<string, unknown>>(
		(data as Record<string, unknown>).parentEntities,
	).map((p) =>
		pick(p, [
			"creditHqParentEntityIdentifier",
			"parentEntityNameLongEnglish",
			"ownershipPercentage",
		]),
	)
}

export function extractChildEntitiesRows(data: EntityDAO): TableRow[] {
	return ensureArray<Record<string, unknown>>(
		(data as Record<string, unknown>).childEntities,
	).map((c) =>
		pick(c, [
			"creditHqChildEntityIdentifier",
			"childEntityNameLongEnglish",
			"ownershipPercentage",
		]),
	)
}

export function extractCreditStatusesRows(data: EntityDAO): TableRow[] {
	return ensureArray<Record<string, unknown>>(
		(data as Record<string, unknown>).creditStatuses,
	).map((s) => pick(s, ["entityCreditStatusCode", "entityCreditStatusName"]))
}

export function extractPendingActivitiesRows(data: EntityDAO): TableRow[] {
	return ensureArray<Record<string, unknown>>(
		(data as Record<string, unknown>).pendingActivities,
	).map((a) =>
		pick(a, ["entityPendingActionCode", "entityPendingActionName", "entityInMarket"]),
	)
}

// ---------------------------------------------------------------------------
// Instrument group: fetch + conversion
// ---------------------------------------------------------------------------

export async function fetchInstrumentGroup(id: string): Promise<InstrumentGroupDAO> {
	return http.getInstrumentGroupById(baseUrl(), id)
}

export function instrumentGroupDaoToKeyFields(
	data: InstrumentGroupDAO,
): InstrumentGroupKeyFields {
	const formatDate = (v: string | null | undefined) => {
		if (v == null) return FALLBACK
		const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/)
		return m ? m[1] : FALLBACK
	}
	const formatAmount = (
		n: number | null | undefined,
		c: string | null | undefined,
	) => {
		if (n == null || Number.isNaN(n)) return FALLBACK
		const fmt = n.toLocaleString("en-US", {
			maximumFractionDigits: 0,
			minimumFractionDigits: 0,
		})
		return c && String(c).trim() ? `$${fmt} ${c}` : fmt
	}
	const id = data.creditHqInstrumentGroupIdentifier ?? ""
	const name = data.instrumentGroupName ?? ""
	const effectiveDateStr = data.instrumentGroupEffectiveDate
	const dealAmount = data.dealAmount
	const currency = data.dealAmountCurrencyCode
	const market = data.instrumentGroupMarketName ?? ""
	const typeVal = data.instrumentGroupTypeName ?? ""
	return {
		id,
		name,
		effectiveDate: formatDate(effectiveDateStr),
		amount: formatAmount(dealAmount, currency),
		market: market || FALLBACK,
		type: typeVal || FALLBACK,
	}
}

export function extractKeyFields(
	data: InstrumentGroupDAO,
): InstrumentGroupKeyFields {
	return instrumentGroupDaoToKeyFields(data)
}

export function extractParticipantsRows(
	data: InstrumentGroupDAO,
): InstrumentGroupParticipantRow[] {
	return (data.participants ?? []).map((p) => {
		const item = p as Record<string, unknown>
		return {
			participantTypeName: str(item.participantTypeName),
			entityNameLongEnglish: str(item.entityNameLongEnglish),
		}
	})
}

export function extractUseOfProceedsRows(
	data: InstrumentGroupDAO,
): InstrumentGroupUseOfProceedsRow[] {
	return (data.useOfProceeds ?? []).map((p) => {
		const item = p as Record<string, unknown>
		return { useOfProceedsName: str(item.useOfProceedsName) }
	})
}

export function extractCovenantsRows(
	data: InstrumentGroupDAO,
): InstrumentGroupCovenantRow[] {
	return (data.covenants ?? []).map((c) => {
		const item = c as Record<string, unknown>
		return {
			covenantCategoryName: str(item.covenantCategoryName),
			covenantTypeName: str(item.covenantTypeName),
			isMaintenance: item.isMaintenance === true,
			isIncurrence: item.isIncurrence === true,
		}
	})
}

export function extractInstrumentsRows(
	data: InstrumentGroupDAO,
): InstrumentGroupInstrumentRow[] {
	return (data.instruments ?? []).map((i) => {
		const item = i as Record<string, unknown>
		return { creditHqInstrumentIdentifier: str(item.creditHqInstrumentIdentifier) }
	})
}

// ---------------------------------------------------------------------------
// Other resources: fetch only (return DAO; add conversion when UI needs it)
// ---------------------------------------------------------------------------

export async function fetchInstrument(id: string): Promise<unknown> {
	return http.getInstrumentById(baseUrl(), id)
}

export async function fetchFund(id: string): Promise<unknown> {
	return http.getFundByCode(baseUrl(), id)
}

export async function searchFundsByName(name: string): Promise<unknown[]> {
	return http.searchFunds(baseUrl(), name)
}

export async function searchEntitiesByName(name: string): Promise<unknown[]> {
	return http.searchEntities(baseUrl(), name)
}

export async function searchInstrumentGroups(name: string): Promise<unknown[]> {
	return http.searchInstrumentGroups(baseUrl(), name)
}

export async function searchInstruments(name: string): Promise<unknown[]> {
	return http.searchInstruments(baseUrl(), name)
}

export async function searchFundParticipants(name: string): Promise<unknown[]> {
	return http.searchFundParticipants(baseUrl(), name)
}

export async function searchFundPipelines(name: string): Promise<unknown[]> {
	return http.searchFundPipelines(baseUrl(), name)
}

export async function searchRates(name: string): Promise<unknown[]> {
	return http.searchRates(baseUrl(), name)
}

export async function searchSectors(name: string): Promise<unknown[]> {
	return http.searchSectors(baseUrl(), name)
}

export async function fetchTrade(id: string): Promise<unknown> {
	return http.getTradeByCode(baseUrl(), id)
}

export async function fetchTradesByInstrument(instrumentIdentifier: string): Promise<unknown> {
	return http.getTradesByInstrument(baseUrl(), instrumentIdentifier)
}

export async function fetchValuation(id: string): Promise<unknown> {
	return http.getValuationByCode(baseUrl(), id)
}

export async function fetchValuationsByInstrument(instrumentIdentifier: string): Promise<unknown> {
	return http.getValuationsByInstrument(baseUrl(), instrumentIdentifier)
}

export async function fetchFundTrade(id: string): Promise<unknown> {
	return http.getFundTradeByCode(baseUrl(), id)
}

export async function fetchFundPipeline(id: string): Promise<unknown> {
	return http.getFundPipelineByCode(baseUrl(), id)
}

export async function fetchFundParticipants(fundId: string, jqParam?: string): Promise<unknown> {
	return http.getFundParticipantsByFundCode(baseUrl(), fundId, jqParam?.trim() ? { jq: jqParam.trim() } : undefined)
}

export async function fetchFundAllocations(
	fundTradeId: string,
	jqParam?: string,
): Promise<unknown> {
	return http.getFundAllocationsByFundTradeCode(baseUrl(), fundTradeId, jqParam?.trim() ? { jq: jqParam.trim() } : undefined)
}

export async function fetchQuotes(instrumentId: string, jqParam?: string): Promise<unknown> {
	return http.getQuotesByInstrumentCode(baseUrl(), instrumentId, jqParam?.trim() ? { jq: jqParam.trim() } : undefined)
}

export async function fetchRateObservations(rateCode: string): Promise<unknown> {
	return http.getRateObservations(baseUrl(), rateCode)
}

export async function fetchPositions(
	fundId: string,
	jqParam?: string,
): Promise<unknown> {
	const params =
		jqParam != null && jqParam.trim() !== ""
			? { jq: jqParam.trim() }
			: undefined
	return http.getPositionsByFund(baseUrl(), fundId, params)
}

/** List funds (optionally with jq filter). Used e.g. for Reporting from Instrument Group. */
export async function fetchFunds(jqParam?: string): Promise<unknown[]> {
	const data = await http.getFunds(baseUrl(), jqParam?.trim() ? { jq: jqParam.trim() } : undefined)
	return Array.isArray(data) ? data : []
}

/** Positions by instrument (JQable from Instrument lookup). */
export async function fetchPositionsByInstrument(
	instrumentIdentifier: string,
	jqParam?: string,
): Promise<unknown[]> {
	const params = jqParam?.trim() ? { jq: jqParam.trim() } : undefined
	const data = await http.getPositionsByInstrument(baseUrl(), instrumentIdentifier, params)
	return Array.isArray(data) ? data : []
}

/** @deprecated Use EntityDAO from ./types. */
export type EntityApiResponse = EntityDAO
/** @deprecated Use InstrumentGroupDAO from ./types. */
export type InstrumentGroupApiResponse = InstrumentGroupDAO
